/*
 * Variant selector for snippets/product-variant-selector.liquid.
 * Moved out of an inline <script> (~72KB re-sent in every product page's HTML,
 * uncacheable). The snippet queues a per-instance config in window.__tvsQueue;
 * this file (deferred, after theme.js - same order the inline code's
 * DOMContentLoaded init had) runs each queued instance. Quick-view fragments
 * injected later push to the queue and call flush() if this file is loaded.
 */
(function () {
  'use strict';

  function run(cfg) {
      'use strict';

      // ========================================
      // CONFIGURATION & ELEMENT REFERENCES
      // ========================================
      const CONFIG = {
        productId: cfg.productId,
        sectionId: cfg.sectionId,
        formId: cfg.formId,
        scope: document.getElementById('product-info-' + cfg.productId + '-' + cfg.sectionId),
        soldOutText: cfg.soldOutText,
        addToCartText: cfg.addToCartText,
        preOrderText: cfg.preOrderText,
        isPreOrder: cfg.isPreOrder
      };

      if (!CONFIG.scope) return;

      const form = CONFIG.scope.querySelector('form#' + CONFIG.formId);
      if (!form) return;

      // Deduplication: abort previous instance to prevent listener stacking
      // when section re-rendering causes this script to re-evaluate
      var _acKey = '__vs_' + CONFIG.sectionId;
      if (window[_acKey]) {
        try { window[_acKey].abort(); } catch(e) {}
      }
      var _ac = new AbortController();
      window[_acKey] = _ac;
      var _sig = { signal: _ac.signal };
      var _sigCapture = { signal: _ac.signal, capture: true };

      // Load variant data
      let variantsSimple = [];
      let variantsFull = [];
      let variantMediaMap = {};
      try {
        variantsSimple = JSON.parse(document.getElementById('variants-' + CONFIG.sectionId).textContent || '[]');
        variantsFull = JSON.parse(document.getElementById('full-variants-' + CONFIG.sectionId).textContent || '[]');
        variantMediaMap = JSON.parse(document.getElementById('variant-media-' + CONFIG.sectionId).textContent || '{}');
      } catch (e) {}

      // ========================================
      // UTILITY FUNCTIONS
      // ========================================
      const Utils = {
        cleanDisplayValue: (value) => {
          return (value || '').replace(/\s*\(Out of Stock\)$/, '');
        },

        getOptionValue: (element) => {
          if (element.tagName === 'SELECT') {
            const opt = element.selectedOptions && element.selectedOptions[0];
            return opt ? ((opt.dataset && opt.dataset.value) || opt.textContent || '').trim() : '';
          } else if (element.type === 'radio') {
            // Only return a value for the checked radio; unchecked radios
            // must return '' so they don't overwrite the real selection.
            if (!element.checked) return '';
            return ((element.dataset && element.dataset.value) || element.value || '').trim();
          }
          // Hidden inputs (dropdown swatches) and other input types
          return ((element.dataset && element.dataset.value) || element.value || '').trim();
        },

        debounce: (func, wait) => {
          let timeout;
          return function executedFunction(...args) {
            const later = () => {
              clearTimeout(timeout);
              func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
          };
        }
      };

      // ========================================
      // CART UTILITIES
      // ========================================
      const CartUtils = {
        addItemsToCart: async function(mainItem, secondaryItem, buttons, propertyMatcher, expeditedItem) {
          // Disable buttons
          buttons.forEach(btn => {
            btn.disabled = true;
            btn.textContent = btn.textContent.replace(/Add to .*|Buy.*/i, 'Adding...');
          });

          try {
            // Step 1: Add main product
            const mainResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ items: [mainItem] })
            });

            const mainData = await mainResponse.json();

            if (!mainResponse.ok && mainResponse.status !== 422) {
              throw new Error('Failed to add main product: ' + (mainData.message || mainData.description));
            }

            // Step 2: Add secondary item if exists
            if (secondaryItem) {
              const secondaryResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({ items: [secondaryItem] })
              });

              const secondaryData = await secondaryResponse.json();
            }

            // Step 3: Add expedited backing if exists
            if (expeditedItem) {
              const expeditedResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({ items: [expeditedItem] })
              });

              const expeditedData = await expeditedResponse.json();
            }

            // Redirect to cart
            window.location.href = '/cart';

          } catch (error) {
            alert('Error adding items to cart: ' + error.message);

            // Re-enable buttons
            buttons.forEach(btn => {
              btn.disabled = false;
              const originalText = btn.getAttribute('data-original-text');
              if (originalText) {
                btn.textContent = originalText;
              } else {
                btn.textContent = btn.textContent.replace('Adding...', 'Add to Cart');
              }
            });
          }
        }
      };

      // ========================================
      // MODULE 1: DROPDOWN OPTION FILTERING
      // ========================================
      const DropdownFilter = {
        selects: Array.from(form.querySelectorAll('select.product-form__single-selector')),
        isSyncing: false,

        init: function() {
          if (this.selects.length === 0) return;

          // Store original options for each select
          this.selects.forEach(sel => {
            if (!sel._allOptions) {
              sel._allOptions = Array.from(sel.options).map(o => o.cloneNode(true));
            }
          });

          // Add change listeners
          this.selects.forEach((sel, idx) => {
            sel.addEventListener('change', () => this.syncFrom(idx + 1));
          });

          // Initial sync
          this.syncFrom(0);
        },

        getCurrentSelections: function() {
          return this.selects.map(sel => {
            const opt = sel.selectedOptions && sel.selectedOptions[0];
            return opt ? (opt.dataset?.value || opt.textContent || '').trim() : '';
          });
        },

        getAllowedValuesAt: function(idx, selections) {
          const set = new Set();
          variantsSimple.forEach(v => {
            for (let i = 0; i < idx; i++) {
              if (selections[i] && v.options[i] !== selections[i]) return;
            }
            set.add(v.options[idx]);
          });
          return Array.from(set);
        },

        isValueAvailable: function(idx, value, selections) {
          return variantsSimple.some(v => {
            if (!v.available) return false;
            if (v.options[idx] !== value) return false;
            for (let i = 0; i < idx; i++) {
              if (selections[i] && v.options[i] !== selections[i]) return false;
            }
            return true;
          });
        },

        repopulateSelect: function(sel, allowed, preferredValue, selectIndex, selections) {
          const prevValue = sel.value;
          const frag = document.createDocumentFragment();

          sel._allOptions.forEach(o => {
            const display = (o.dataset?.value || o.textContent || '').trim();
            const cleanDisplay = Utils.cleanDisplayValue(display);

            if (allowed.indexOf(cleanDisplay) !== -1) {
              const copy = o.cloneNode(true);
              const available = this.isValueAvailable(selectIndex, cleanDisplay, selections);

              if (!available) {
                copy.textContent = cleanDisplay + ' (Out of Stock)';
                copy.disabled = false;
                copy.classList.add('is-disabled');
              } else {
                copy.textContent = cleanDisplay;
                copy.disabled = false;
                copy.hidden = false;
                copy.classList.remove('is-disabled');
              }
              frag.appendChild(copy);
            }
          });

          // Fallback: restore all if nothing matched
          if (!frag.childNodes.length) {
            sel._allOptions.forEach(o => frag.appendChild(o.cloneNode(true)));
          }

          sel.innerHTML = '';
          sel.appendChild(frag);

          // Select preferred value or first available
          const toSelect = Array.from(sel.options).find(o => {
            const cleanDisplay = Utils.cleanDisplayValue((o.dataset?.value || o.textContent || '').trim());
            return cleanDisplay === preferredValue;
          }) || Array.from(sel.options).find(o => !o.classList.contains('is-disabled')) || sel.options[0];

          if (toSelect) sel.value = toSelect.value;

          return prevValue !== sel.value;
        },

        syncFrom: function(startIdx) {
          if (this.isSyncing) return;
          this.isSyncing = true;

          const selections = this.getCurrentSelections();
          let anyChanged = false;

          for (let i = startIdx; i < this.selects.length; i++) {
            const sel = this.selects[i];
            const allowed = this.getAllowedValuesAt(i, selections);
            const changed = this.repopulateSelect(sel, allowed, selections[i], i, selections);
            anyChanged = anyChanged || changed;

            // Update selection with clean value
            const opt = sel.selectedOptions && sel.selectedOptions[0];
            const rawText = opt ? opt.textContent.trim() : '';
            selections[i] = Utils.cleanDisplayValue(rawText);
          }

          // Trigger change event for downstream listeners
          if (anyChanged && this.selects.length) {
            const last = this.selects[this.selects.length - 1];
            last.dispatchEvent(new Event('input', { bubbles: true }));
            last.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.isSyncing = false;
        }
      };

      // ========================================
      // MODULE 1B: DROPDOWN SWATCH OPTION FILTERING
      // ========================================
      // Mirrors DropdownFilter but for dropdown_swatch selectors
      // (hidden input + custom <ul> listbox), supporting up to 3 options.
      const DropdownSwatchFilter = {
        containers: Array.from(form.querySelectorAll('[data-dropdown-swatch]')),
        isSyncing: false,

        init: function() {
          if (this.containers.length === 0) return;

          // Cache position for each container and pre-sort by position
          // so syncFrom doesn't re-query/re-sort on every call
          this._posCache = new Map();
          this.containers.forEach(function(c) {
            var input = c.querySelector('input[type="hidden"].product-form__single-selector');
            this._posCache.set(c, input ? (parseInt(input.getAttribute('data-option-position'), 10) || 1) - 1 : -1);
          }.bind(this));
          this._sorted = this.containers.slice().sort(function(a, b) {
            return this._posCache.get(a) - this._posCache.get(b);
          }.bind(this));

          // Listen for changes on ALL option inputs so mixed selector types
          // (e.g. radio swatch + dropdown swatch) trigger filtering
          var allInputs = form.querySelectorAll('.product-form__single-selector[name^="option"]');
          allInputs.forEach(el => {
            el.addEventListener('change', () => this.syncFrom(0), _sig);
          });

          // Initial sync
          this.syncFrom(0);
        },

        // Get option position (0-based) for a container (cached)
        getPosition: function(container) {
          return this._posCache ? this._posCache.get(container) : -1;
        },

        // Get the currently selected value for a container
        getSelectedValue: function(container) {
          const input = container.querySelector('input[type="hidden"].product-form__single-selector');
          return input ? ((input.dataset && input.dataset.value) || '').trim() : '';
        },

        // Build selections array [opt0, opt1, opt2] from ALL option inputs
        // (handles mixed selector types: swatch radios, dropdowns, dropdown swatches)
        getCurrentSelections: function() {
          const byPos = {};
          var allInputs = form.querySelectorAll('.product-form__single-selector[name^="option"]');
          allInputs.forEach(function(el) {
            var pos = (parseInt(el.getAttribute('data-option-position'), 10) || 1) - 1;
            var value = Utils.getOptionValue(el);
            if (value) byPos[pos] = value;
          });
          return [byPos[0] || '', byPos[1] || '', byPos[2] || ''];
        },

        // Check if a value at a given option index is available given current
        // selections in all OTHER positions (not just earlier ones)
        isValueAvailable: function(idx, value, selections) {
          return variantsSimple.some(function(v) {
            if (!v.available) return false;
            if (v.options[idx] !== value) return false;
            for (var i = 0; i < selections.length; i++) {
              if (i === idx) continue;
              if (selections[i] && v.options[i] !== selections[i]) return false;
            }
            return true;
          });
        },

        // Check if a value at a given option index exists in any variant
        // given current selections in all OTHER positions
        valueExists: function(idx, value, selections) {
          return variantsSimple.some(function(v) {
            if (v.options[idx] !== value) return false;
            for (var i = 0; i < selections.length; i++) {
              if (i === idx) continue;
              if (selections[i] && v.options[i] !== selections[i]) return false;
            }
            return true;
          });
        },

        syncFrom: function(startIdx) {
          if (this.isSyncing) return;
          this.isSyncing = true;

          var selections = this.getCurrentSelections();

          this._sorted.forEach(function(container) {
            var pos = this.getPosition(container);
            if (pos < 0) return;

            var listbox = container.querySelector('[data-dropdown-swatch-listbox]');
            if (!listbox) return;

            var options = Array.from(listbox.querySelectorAll('[role="option"]'));

            // Build exists/available maps in one pass over variantsSimple
            // instead of scanning per-option (O(variants) vs O(options * variants))
            var existsMap = {};
            var availableMap = {};
            for (var v = 0; v < variantsSimple.length; v++) {
              var variant = variantsSimple[v];
              if (variant.options[pos] === undefined) continue;
              var matches = true;
              for (var s = 0; s < selections.length; s++) {
                if (s === pos) continue;
                if (selections[s] && variant.options[s] !== selections[s]) { matches = false; break; }
              }
              if (matches) {
                existsMap[variant.options[pos]] = true;
                if (variant.available) availableMap[variant.options[pos]] = true;
              }
            }

            // --- Pass 1: visibility / disabled state ---
            options.forEach(function(li) {
              var value = (li.getAttribute('data-value') || '').trim();
              var textEl = li.querySelector('.dropdown-swatch__option-text');
              var exists = !!existsMap[value];
              var available = !!availableMap[value];

              // Hide options that don't exist for current selections
              if (!exists) {
                li.style.display = 'none';
              } else {
                li.style.display = '';
              }

              // Toggle disabled class and sold-out text
              if (!available && exists) {
                li.classList.add('dropdown-swatch__option--disabled');
                if (textEl && textEl.textContent.indexOf('(Sold Out)') === -1) {
                  textEl.textContent = value + ' (Sold Out)';
                }
              } else {
                li.classList.remove('dropdown-swatch__option--disabled');
                if (textEl) {
                  textEl.textContent = value;
                }
              }
            });

            // --- Pass 2: auto-select if current value no longer exists ---
            var currentValue = selections[pos];
            if (currentValue && !existsMap[currentValue]) {
              // Find best replacement: prefer first visible in-stock, then first visible sold-out
              var firstInStock = null;
              var firstSoldOut = null;
              for (var i = 0; i < options.length; i++) {
                var li = options[i];
                if (li.style.display === 'none') continue; // hidden = doesn't exist
                if (!firstSoldOut) firstSoldOut = li; // first visible option (may be sold out)
                if (!li.classList.contains('dropdown-swatch__option--disabled')) {
                  firstInStock = li;
                  break; // in-stock is best, stop searching
                }
              }

              var replacement = firstInStock || firstSoldOut;
              if (replacement) {
                var newValue = (replacement.getAttribute('data-value') || '').trim();
                var newValueId = replacement.getAttribute('data-value-id') || '';

                // Update listbox selected state
                options.forEach(function(opt) {
                  opt.classList.remove('is-selected');
                  opt.setAttribute('aria-selected', 'false');
                });
                replacement.classList.add('is-selected');
                replacement.setAttribute('aria-selected', 'true');

                // Update trigger display (mirrors selectOption in custom.js)
                var trigger = container.querySelector('[data-dropdown-swatch-trigger]');
                if (trigger) {
                  var triggerSwatch = trigger.querySelector('.dropdown-swatch__swatch');
                  var triggerText = trigger.querySelector('.dropdown-swatch__trigger-text');
                  var optionSwatch = replacement.querySelector('.dropdown-swatch__option-swatch');

                  if (triggerSwatch && optionSwatch) {
                    triggerSwatch.setAttribute('style', optionSwatch.getAttribute('style'));
                    var isWhite = replacement.classList.contains('dropdown-swatch__option--white-swatch');
                    triggerSwatch.classList.toggle('dropdown-swatch__swatch--white', isWhite);
                    var isNoMatch = optionSwatch.classList.contains('dropdown-swatch__option-swatch--no-match');
                    triggerSwatch.classList.toggle('dropdown-swatch__swatch--no-match', isNoMatch);
                  }
                  if (triggerText) {
                    triggerText.textContent = newValue;
                  }
                }

                // Update hidden input
                var hiddenInput = container.querySelector('input[type="hidden"].product-form__single-selector');
                if (hiddenInput) {
                  hiddenInput.value = newValueId;
                  hiddenInput.setAttribute('data-value', newValue);
                  hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
                }

                // Update selections so downstream containers see the new value
                selections[pos] = newValue;
              }
            }
          }.bind(this));

          this.isSyncing = false;
        }
      };

      // ========================================
      // MODULE 2: VARIANT ID SYNCHRONIZATION
      // ========================================
      const VariantSync = {
        optionInputs: Array.from(form.querySelectorAll('.product-form__single-selector[name^="option"]')),
        idSelect: form.querySelector('#product-select-' + CONFIG.productId + '[name="id"]'),

        init: function() {
          this.optionInputs.forEach(el => {
            el.addEventListener('change', () => this.deferredSync(), _sig);
          });
          this.deferredSync();
        },

        getCurrentSelections: function() {
          const byPos = {};
          this.optionInputs.forEach(el => {
            const pos = (parseInt(el.getAttribute('data-option-position'), 10) || 1) - 1;
            const value = Utils.getOptionValue(el);
            if (value) byPos[pos] = value;
          });
          return [byPos[0] || '', byPos[1] || '', byPos[2] || ''];
        },

        findVariantByOptions: function(opts) {
          return variantsFull.find(v =>
            (v.options[0] || '') === opts[0] &&
            (v.options[1] || '') === opts[1] &&
            (v.options[2] || '') === opts[2]
          );
        },

        syncId: function() {
          const opts = this.getCurrentSelections();
          const variant = this.findVariantByOptions(opts);
          if (!variant || !this.idSelect) return;

          const newId = String(variant.id);
          if (this.idSelect.value !== newId) {
            // Update URL
            try {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', newId);
              window.history.replaceState({}, '', url);
            } catch (e) {}

            // Update hidden select
            this.idSelect.value = newId;
            this.idSelect.dispatchEvent(new Event('input', { bubbles: true }));
            this.idSelect.dispatchEvent(new Event('change', { bubbles: true }));

            // Augment variant with featured_media from Liquid-generated mapping
            // (product.variants JSON does not include featured_media)
            var mediaId = variantMediaMap[String(variant.id)];
            if (mediaId && !variant.featured_media) {
              variant.featured_media = { id: mediaId };
            }

            // Broadcast custom events
            document.dispatchEvent(new CustomEvent('product:variant:change', { detail: { variant } }));
            document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));

            // Dispatch on form so the gallery updates (bubbles to ProductSection)
            form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
          }
        },

        deferredSync: function() {
          clearTimeout(this._syncTimer);
          this._syncTimer = setTimeout(() => {
            this.syncId();
          }, 0);
        }
      };

      // ========================================
      // MODULE 3: ADD TO CART VISIBILITY
      // ========================================
      const CartButtonVisibility = {
        lastAvailability: null,

        init: function() {
          document.addEventListener('product:variant:change', e => {
            if (e.detail?.variant) this.update(e.detail.variant);
          }, _sig);
          document.addEventListener('variant:changed', e => {
            if (e.detail?.variant) this.update(e.detail.variant);
          }, _sig);
          document.addEventListener('product:rerendered', () => this.checkCurrent(), _sig);
          document.addEventListener('shopify:section:load', () => this.checkCurrent(), _sig);

          this.checkCurrent();
        },

        getButton: function() {
          return form.querySelector('.product-form__add-button') ||
                 form.querySelector('button[type="submit"][name="add"]') ||
                 form.querySelector('button[type="submit"]') ||
                 form.querySelector('.product-form__submit') ||
                 CONFIG.scope.querySelector('button[type="submit"]');
        },

        update: function(variant, immediate = false) {
          if (!variant) return;

          const btn = this.getButton();
          if (!btn) return;

          // Check if button is controlled by backing options
          const isBackingControlled = btn.getAttribute('data-backing-controlled') === 'true' ||
                                       btn.getAttribute('data-bulk-backing-controlled') === 'true';

          const isSoldOut = btn.classList.contains('variant-sold-out-hide');

          // Skip if no change
          if (this.lastAvailability === variant.available && isSoldOut === !variant.available) {
            return;
          }

          this.lastAvailability = variant.available;

          if (!variant.available) {
            if (!isSoldOut) {
              btn.classList.add('variant-sold-out-hide');
              btn.setAttribute('data-sold-out', 'true');
              btn.disabled = true;
              btn.textContent = CONFIG.soldOutText;
              btn.classList.remove('button--primary');
              btn.classList.add('button--disabled');
              btn.type = 'button';
            }
          } else {
            if (isSoldOut && !isBackingControlled) {
              const show = () => {
                btn.classList.remove('variant-sold-out-hide');
                btn.removeAttribute('data-sold-out');
                btn.disabled = false;
                btn.textContent = CONFIG.isPreOrder ? CONFIG.preOrderText : CONFIG.addToCartText;
                btn.classList.remove('button--disabled');
                btn.classList.add('button--primary');
                btn.type = 'submit';
              };
              immediate ? show() : setTimeout(show, 50);
            } else if (isSoldOut && isBackingControlled) {
              btn.classList.remove('variant-sold-out-hide');
              btn.removeAttribute('data-sold-out');
              btn.textContent = CONFIG.isPreOrder ? CONFIG.preOrderText : CONFIG.addToCartText;
              btn.classList.remove('button--disabled');
              btn.classList.add('button--primary');
              btn.type = 'submit';
            }
          }
        },

        checkCurrent: function() {

          // Derive the real variant from option inputs — NOT the hidden select,
          // because after a section rerender the server sets the hidden select
          // to product.selected_or_first_available_variant (always in-stock).
          var inputs = form.querySelectorAll('.product-form__single-selector[name^="option"]');
          var variant = null;

          if (inputs.length) {
            var byPos = {};
            inputs.forEach(function(el) {
              var pos = (parseInt(el.getAttribute('data-option-position'), 10) || 1) - 1;
              var val = Utils.cleanDisplayValue(Utils.getOptionValue(el));
              if (val) byPos[pos] = val;
            });
            var opts = [byPos[0] || '', byPos[1] || '', byPos[2] || ''];

            variant = variantsFull.find(function(v) {
              return (v.options[0] || '') === opts[0] &&
                     (v.options[1] || '') === opts[1] &&
                     (v.options[2] || '') === opts[2];
            });
          }

          // Fallback to hidden select if option inputs not found
          if (!variant) {
            var idSelect = form.querySelector('#product-select-' + CONFIG.productId + '[name="id"]');
            if (idSelect?.value) {
              variant = variantsFull.find(v => String(v.id) === String(idSelect.value));
            }
          }

          if (variant) {
            // Also fix the hidden select so it matches
            var idSel = form.querySelector('select[name="id"]');
            if (idSel && idSel.value !== String(variant.id)) {
              idSel.value = String(variant.id);
            }
            this.update(variant, true);

            // Re-trigger Notify Me app if variant is OOS
            if (!variant.available && idSel) {
              idSel.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      };

      // ========================================
      // MODULE 4: AFFIRM PAYMENT INTEGRATION
      // ========================================
      const AffirmIntegration = {
        init: function() {
          // Refresh Affirm on variant changes
          const events = ['variant:changed', 'product:variant:change', 'product:rerendered',
                         'shopify:section:load', 'shopify:section:select'];
          events.forEach(evt => document.addEventListener(evt, () => this.refresh(), _sig));

          document.addEventListener('change', e => {
            if (e.target?.matches('select.product-form__single-selector')) {
              setTimeout(() => this.refresh(), 0);
            }
          }, _sig);

          // Position Affirm widget
          this.positionWidget();
          document.addEventListener('DOMContentLoaded', () => this.positionWidget(), _sig);
          events.forEach(evt => document.addEventListener(evt, () => this.positionWidget(), _sig));

          // Watch for DOM changes (disconnect on abort to prevent stacking)
          const target = document.getElementById('shopify-section-' + CONFIG.sectionId) || document.body;
          const obs = new MutationObserver(() => this.positionWidget());
          obs.observe(target, { childList: true, subtree: true });
          _ac.signal.addEventListener('abort', () => obs.disconnect());
        },

        refresh: function() {
          if (window.affirm?.ui?.refresh) {
            try {
              affirm.ui.refresh();
            } catch (e) {}
          }
        },

        positionWidget: function() {
          const anchor = document.querySelector('#affirm-under-price');
          if (!anchor) return;

          const section = document.getElementById('shopify-section-' + CONFIG.sectionId) || document;
          const widget = section.querySelector('.affirm-as-low-as');

          if (widget && widget.parentElement !== anchor) {
            const originalParent = widget.parentElement;

            anchor.innerHTML = '';
            anchor.appendChild(widget);

            if (originalParent?.classList.contains('shopify-app-block')) {
              originalParent.style.display = 'none';
            }

            this.refresh();
          }
        }
      };

      // ========================================
      // MODULE 5: BACKING OPTIONS
      // ========================================
      const BackingOptions = {
        retrieveSelect: null,
        backingSelect: null,
        buttons: [],
        paymentButtonContainer: null,
        helperText: null,
        isUpdating: false,
        observers: [],

        init: function() {
          this.retrieveSelect = CONFIG.scope.querySelector('.backing-retrieve-select');
          this.backingSelect = CONFIG.scope.querySelector('.backing-choice-select');

          if (!this.retrieveSelect || !this.backingSelect) {
            return;
          }

          // Find all buttons
          const addToCartBtn = form.querySelector('button[type="submit"][name="add"]') ||
                              form.querySelector('button[type="submit"]') ||
                              CONFIG.scope.querySelector('button[type="submit"]');
          if (addToCartBtn) this.buttons.push(addToCartBtn);

          const buyNowButtons = CONFIG.scope.querySelectorAll('.shopify-payment-button__button');
          buyNowButtons.forEach(btn => this.buttons.push(btn));

          this.paymentButtonContainer = CONFIG.scope.querySelector('.shopify-payment-button');

          if (this.buttons.length === 0) return;

          // Create helper text element if it doesn't exist
          this.createHelperText();

          // Mark buttons as controlled
          this.buttons.forEach(btn => {
            btn.setAttribute('data-backing-controlled', 'true');
            if (!btn.getAttribute('data-original-text')) {
              btn.setAttribute('data-original-text', btn.textContent);
            }
          });

          // Setup event handlers
          this.setupClickHandlers();
          this.setupFormHandler();
          this.setupChangeListeners();
          this.setupMutationObservers();
          this.setupEventListeners();

          // Initial state
          this.updateButtonState();
        },

        createHelperText: function() {
          // Check if helper text already exists
          const existingHelper = form.querySelector('.product-form__button-helper[data-backing-helper]');
          if (existingHelper) {
            this.helperText = existingHelper;
            this.helperText.classList.remove('show'); // Ensure it's hidden initially
            return;
          }

          // Create helper text element
          this.helperText = document.createElement('div');
          this.helperText.className = 'product-form__button-helper';
          this.helperText.setAttribute('data-backing-helper', 'true');
          this.helperText.textContent = 'Please select all product options first';
          // Explicitly ensure it starts hidden (CSS should handle this, but be explicit)
          this.helperText.style.display = 'none';

          // Insert BEFORE the first button or payment button container
          const insertBefore = this.buttons[0] || this.paymentButtonContainer;
          if (insertBefore && insertBefore.parentNode) {
            insertBefore.parentNode.insertBefore(this.helperText, insertBefore);
          }
        },

        setupClickHandlers: function() {
          this.buttons.forEach(btn => {
            btn.addEventListener('click', e => this.handleButtonClick(e), true);
          });

          if (this.paymentButtonContainer) {
            this.paymentButtonContainer.addEventListener('click', e => {
              if (!this.isValid()) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                alert('Please select both Retrieve Hand and Backing Choice options.');
                return false;
              }
            }, true);
          }
        },

        setupFormHandler: function() {
          form.addEventListener('submit', e => {
            // Check for expedited backing from dropdown option
            const selectedOption = this.backingSelect.options[this.backingSelect.selectedIndex];
            const isExpedited = selectedOption.getAttribute('data-expedited') === 'true';

            if (!this.isValid()) {
              e.preventDefault();

              // Show helper text and flash it
              if (this.helperText) {
                this.helperText.classList.add('show');
                this.helperText.style.animation = 'none';
                setTimeout(() => {
                  this.helperText.style.animation = 'pulse 0.5s ease-in-out 2';
                }, 10);
              }

              alert('Please select all product options first (Retrieve Hand and Backing Choice).');
              return false;
            }

            if (this.isPaidBacking()) {
              e.preventDefault();
              this.handlePaidBackingSubmit();
              return false;
            }

            // Handle included backing with expedited option
            if (isExpedited) {
              e.preventDefault();
              this.handleIncludedBackingWithExpedited();
              return false;
            }
          }, true);
        },

        handleIncludedBackingWithExpedited: function() {
          const selectedOption = this.backingSelect.options[this.backingSelect.selectedIndex];
          const retrieveValue = this.retrieveSelect.value;
          const backingValue = this.backingSelect.value;

          // Get expedited backing variant matching the selected retrieve hand
          const expeditedVariantId = this.getExpeditedVariantId();

          // Get main variant ID
          const idSelect = form.querySelector('#product-select-' + CONFIG.productId + '[name="id"]');
          const idInput = form.querySelector('input[name="id"]');
          const mainVariantId = idSelect?.value || idInput?.value;

          // Get quantity from form
          const qtyInput = form.querySelector('input[name="quantity"]') ||
                          form.querySelector('input[type="number"]') ||
                          form.querySelector('.quantity__input');
          const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          if (!mainVariantId) {
            alert('Error: Could not determine the product variant. Please refresh and try again.');
            return;
          }

          // Prepare main item with all properties including expedited backing
          const mainItem = {
            id: mainVariantId,
            quantity: quantity,
            properties: {
              'Retrieve Hand': retrieveValue,
              'Backing Choice': backingValue,
              'Expedited Backing': 'Yes'
            }
          };

          // Prepare expedited backing item
          const expeditedItem = {
            id: expeditedVariantId,
            quantity: quantity,
            properties: {
              '_Related to': 'Main Product',
              'Type': 'Expedited Backing'
            }
          };

          // Property matcher for cart verification
          const propertyMatcher = (cartProps, itemProps) => {
            return cartProps &&
                   cartProps['Retrieve Hand'] === itemProps['Retrieve Hand'] &&
                   cartProps['Backing Choice'] === itemProps['Backing Choice'];
          };

          // Use shared cart utility (no secondary item for included backing)
          CartUtils.addItemsToCart(mainItem, null, this.buttons, propertyMatcher, expeditedItem)
            .then(() => {
              this.updateButtonState();
            })
            .catch(() => {
              this.updateButtonState();
            });
        },

        setupChangeListeners: function() {
          this.retrieveSelect.addEventListener('change', () => this.updateButtonState());
          this.backingSelect.addEventListener('change', () => this.updateButtonState());
        },

        setupMutationObservers: function() {
          this.buttons.forEach(btn => {
            const observer = new MutationObserver(mutations => {
              let needsUpdate = false;
              mutations.forEach(mutation => {
                if (mutation.type === 'attributes' &&
                    (mutation.attributeName === 'disabled' || mutation.attributeName === 'aria-disabled')) {
                  if (!this.isValid() && !btn.disabled) {
                    needsUpdate = true;
                  }
                }
              });
              if (needsUpdate && !this.isUpdating) {
                this.updateButtonState();
              }
            });

            observer.observe(btn, {
              attributes: true,
              attributeFilter: ['disabled', 'aria-disabled']
            });

            this.observers.push(observer);
          });
        },

        setupEventListeners: function() {
          const enforceState = () => {
            this.updateButtonState();
            // Use immediate re-checks on variant changes
            [0, 0, 5, 10, 20].forEach(delay => {
              setTimeout(() => this.updateButtonState(), delay);
            });
          };

          const events = ['variant:change', 'variant:changed', 'product:variant:change',
                         'shopify:section:load', 'product:rerendered'];
          events.forEach(evt => document.addEventListener(evt, enforceState, _sigCapture));

          document.addEventListener('input', e => {
            if (e.target?.classList.contains('product-form__single-selector') || e.target?.name === 'id') {
              enforceState();
            }
          }, _sigCapture);

          document.addEventListener('change', e => {
            if (e.target?.classList.contains('product-form__single-selector') || e.target?.name === 'id') {
              enforceState();
            }
          }, _sigCapture);

          // Aggressive periodic check (clear on abort to prevent stacking)
          var _backingInterval = setInterval(() => {
            if (!this.isValid()) {
              this.buttons.forEach(btn => {
                if (!btn.disabled || btn.getAttribute('aria-disabled') !== 'true') {
                  btn.disabled = true;
                  btn.setAttribute('disabled', 'disabled');
                  btn.setAttribute('aria-disabled', 'true');
                  btn.classList.add('backing-disabled');
                }
              });

              if (this.paymentButtonContainer) {
                this.paymentButtonContainer.style.pointerEvents = 'none';
                this.paymentButtonContainer.style.opacity = '0.5';
              }
            }
          }, 50);
          _ac.signal.addEventListener('abort', () => clearInterval(_backingInterval));
        },

        isValid: function() {
          return !!(this.retrieveSelect.value && this.backingSelect.value);
        },

        isPaidBacking: function() {
          const container = CONFIG.scope.querySelector('.product-form__backing-options');
          return container && container.getAttribute('data-backing-type') === 'paid';
        },

        getExpeditedVariantId: function() {
          const hand = this.retrieveSelect.value === 'RH' ? 'rh' : 'lh';
          return parseInt(this.backingSelect.getAttribute('data-expedited-variant-id-' + hand), 10);
        },

        handleButtonClick: function(e) {
          if (!this.isValid()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Show helper text and flash it
            if (this.helperText) {
              this.helperText.style.display = 'block';
              this.helperText.classList.add('show');
              this.helperText.style.animation = 'none';
              setTimeout(() => {
                this.helperText.style.animation = 'pulse 0.5s ease-in-out 2';
              }, 10);
            }

            alert('Please select all product options first (Retrieve Hand and Backing Choice).');
            return false;
          }

          // Check for expedited backing from dropdown option
          const selectedOption = this.backingSelect.options[this.backingSelect.selectedIndex];
          const isExpedited = selectedOption.getAttribute('data-expedited') === 'true';

          if (this.isPaidBacking()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handlePaidBackingSubmit();
            return false;
          }

          // Handle included backing with expedited option
          if (isExpedited) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handleIncludedBackingWithExpedited();
            return false;
          }

          // Normal included backing - let form submit normally
        },

        handlePaidBackingSubmit: function() {
          const selectedBackingOption = this.backingSelect.options[this.backingSelect.selectedIndex];
          const backingVariantId = selectedBackingOption.getAttribute('data-variant-id');
          const backingValue = this.backingSelect.value;
          const retrieveValue = this.retrieveSelect.value;

          // Check for expedited backing from dropdown option
          const isExpedited = selectedBackingOption.getAttribute('data-expedited') === 'true';
          const expeditedVariantId = isExpedited ? this.getExpeditedVariantId() : null;

          // Get main variant ID
          const idSelect = form.querySelector('#product-select-' + CONFIG.productId + '[name="id"]');
          const idInput = form.querySelector('input[name="id"]');
          const mainVariantId = idSelect?.value || idInput?.value;

          // Get quantity from form
          const qtyInput = form.querySelector('input[name="quantity"]') ||
                          form.querySelector('input[type="number"]') ||
                          form.querySelector('.quantity__input');
          const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          if (!mainVariantId) {
            alert('Error: Could not determine the product variant. Please refresh and try again.');
            return;
          }

          // Prepare main item properties
          const mainProperties = {
            'Retrieve Hand': retrieveValue,
            'Backing Choice': backingValue
          };
          if (isExpedited) {
            mainProperties['Expedited Backing'] = 'Yes';
          }

          // Prepare main item
          const mainItem = {
            id: mainVariantId,
            quantity: quantity,
            properties: mainProperties
          };

          // Prepare secondary item if applicable
          const secondaryItem = (backingValue !== 'None' && backingVariantId) ? {
            id: backingVariantId,
            quantity: quantity,
            properties: {
              '_Related to': 'Main Product',
              'Type': 'Backing - ' + backingValue
            }
          } : null;

          // Prepare expedited backing item if expedited option selected
          const expeditedItem = (isExpedited && expeditedVariantId) ? {
            id: expeditedVariantId,
            quantity: quantity,
            properties: {
              '_Related to': 'Main Product',
              'Type': 'Expedited Backing'
            }
          } : null;

          // Property matcher for cart verification
          const propertyMatcher = (cartProps, itemProps) => {
            return cartProps &&
                   cartProps['Retrieve Hand'] === itemProps['Retrieve Hand'] &&
                   cartProps['Backing Choice'] === itemProps['Backing Choice'];
          };

          // Use shared cart utility with expedited backing support
          CartUtils.addItemsToCart(mainItem, secondaryItem, this.buttons, propertyMatcher, expeditedItem)
            .then(() => {
              this.updateButtonState();
            })
            .catch(() => {
              this.updateButtonState();
            });
        },

        updateButtonState: function() {
          if (this.isUpdating) return;
          this.isUpdating = true;

          const isValid = this.isValid();

          this.buttons.forEach(btn => {
            // Skip if button is hidden due to sold out variant
            if (btn.classList.contains('variant-sold-out-hide')) {
              this.isUpdating = false;
              return;
            }

            if (isValid) {
              btn.disabled = false;
              btn.removeAttribute('disabled');
              btn.removeAttribute('aria-disabled');
              btn.classList.remove('backing-disabled');
            } else {
              btn.disabled = true;
              btn.setAttribute('disabled', 'disabled');
              btn.setAttribute('aria-disabled', 'true');
              btn.classList.add('backing-disabled');
            }
          });

          if (this.paymentButtonContainer) {
            if (isValid) {
              this.paymentButtonContainer.style.display = '';
              this.paymentButtonContainer.style.pointerEvents = '';
              this.paymentButtonContainer.style.opacity = '';
            } else {
              this.paymentButtonContainer.style.pointerEvents = 'none';
              this.paymentButtonContainer.style.opacity = '0.5';
            }
          }

          // Hide helper text when valid (only show it when user tries to click)
          if (this.helperText && isValid) {
            this.helperText.style.display = 'none';
            this.helperText.classList.remove('show');
          }

          this.isUpdating = false;
        }
      };

      // ========================================
      // MODULE 6: BULK BACKING OPTIONS
      // ========================================
      const BulkBackingOptions = {
        colorSelect: null,
        lbSelect: null,
        lengthSelect: null,
        instructionsSelect: null,
        buttons: [],
        paymentButtonContainer: null,
        helperText: null,
        isUpdating: false,
        observers: [],

        init: function() {
          this.colorSelect = CONFIG.scope.querySelector('.bulk-backing-color-select');
          this.lbSelect = CONFIG.scope.querySelector('.bulk-backing-lb-select');
          this.lengthSelect = CONFIG.scope.querySelector('.bulk-backing-length-select');
          this.instructionsSelect = CONFIG.scope.querySelector('.backing-instructions-select');

          // Only init if at least one of these selects exists
          if (!this.colorSelect && !this.instructionsSelect) return;

          // Find all buttons
          const addToCartBtn = form.querySelector('button[type="submit"][name="add"]') ||
                              form.querySelector('button[type="submit"]') ||
                              CONFIG.scope.querySelector('button[type="submit"]');
          if (addToCartBtn) this.buttons.push(addToCartBtn);

          const buyNowButtons = CONFIG.scope.querySelectorAll('.shopify-payment-button__button');
          buyNowButtons.forEach(btn => this.buttons.push(btn));

          this.paymentButtonContainer = CONFIG.scope.querySelector('.shopify-payment-button');

          if (this.buttons.length === 0) return;

          // Create helper text element if it doesn't exist
          this.createHelperText();

          // Mark buttons as controlled
          this.buttons.forEach(btn => {
            btn.setAttribute('data-bulk-backing-controlled', 'true');
          });

          // Setup event handlers
          this.setupClickHandlers();
          this.setupFormHandler();
          this.setupChangeListeners();
          this.setupMutationObservers();
          this.setupEventListeners();

          // Initial state
          this.updateButtonState();

        },

        createHelperText: function() {
          // Check if helper text already exists
          const existingHelper = form.querySelector('.product-form__button-helper[data-bulk-backing-helper]');
          if (existingHelper) {
            this.helperText = existingHelper;
            this.helperText.classList.remove('show'); // Ensure it's hidden initially
            return;
          }

          // Create helper text element
          this.helperText = document.createElement('div');
          this.helperText.className = 'product-form__button-helper';
          this.helperText.setAttribute('data-bulk-backing-helper', 'true');
          this.helperText.textContent = 'Please select all product options first';
          // Explicitly ensure it starts hidden (CSS should handle this, but be explicit)
          this.helperText.style.display = 'none';

          // Insert BEFORE the first button or payment button container
          const insertBefore = this.buttons[0] || this.paymentButtonContainer;
          if (insertBefore && insertBefore.parentNode) {
            insertBefore.parentNode.insertBefore(this.helperText, insertBefore);
          }
        },

        setupClickHandlers: function() {
          this.buttons.forEach(btn => {
            btn.addEventListener('click', e => this.handleButtonClick(e), true);
          });
        },

        setupFormHandler: function() {
          form.addEventListener('submit', e => {
            if (!this.isValid()) {
              e.preventDefault();

              // Show helper text and flash it
              if (this.helperText) {
                this.helperText.classList.add('show');
                this.helperText.style.animation = 'none';
                setTimeout(() => {
                  this.helperText.style.animation = 'pulse 0.5s ease-in-out 2';
                }, 10);
              }

              alert('Please select all product options first.');
              return false;
            }

            // For bulk backing products, handle custom cart add
            if (this.colorSelect && this.lbSelect && this.lengthSelect) {
              e.preventDefault();
              this.handleBulkBackingSubmit();
              return false;
            }
          }, true);
        },

        setupChangeListeners: function() {
          if (this.colorSelect) {
            this.colorSelect.addEventListener('change', () => this.updateButtonState());
          }
          if (this.lbSelect) {
            this.lbSelect.addEventListener('change', () => this.updateButtonState());
          }
          if (this.lengthSelect) {
            this.lengthSelect.addEventListener('change', () => this.updateButtonState());
          }
          if (this.instructionsSelect) {
            this.instructionsSelect.addEventListener('change', () => this.updateButtonState());
          }
        },

        setupMutationObservers: function() {
          this.buttons.forEach(btn => {
            const observer = new MutationObserver(mutations => {
              let needsUpdate = false;
              mutations.forEach(mutation => {
                if (mutation.type === 'attributes' &&
                    (mutation.attributeName === 'disabled' || mutation.attributeName === 'aria-disabled')) {
                  if (!this.isValid() && !btn.disabled) {
                    needsUpdate = true;
                  }
                }
              });
              if (needsUpdate && !this.isUpdating) {
                this.updateButtonState();
              }
            });

            observer.observe(btn, {
              attributes: true,
              attributeFilter: ['disabled', 'aria-disabled']
            });

            this.observers.push(observer);
          });
        },

        setupEventListeners: function() {
          const enforceState = () => {
            this.updateButtonState();
            // Use immediate re-checks on variant changes
            [0, 0, 5, 10, 20].forEach(delay => {
              setTimeout(() => this.updateButtonState(), delay);
            });
          };

          const events = ['variant:change', 'variant:changed', 'product:variant:change',
                         'shopify:section:load', 'product:rerendered'];
          events.forEach(evt => document.addEventListener(evt, enforceState, _sigCapture));

          // Aggressive periodic check (clear on abort to prevent stacking)
          var _bulkInterval = setInterval(() => {
            if (!this.isValid()) {
              this.buttons.forEach(btn => {
                if (!btn.disabled || btn.getAttribute('aria-disabled') !== 'true') {
                  btn.disabled = true;
                  btn.setAttribute('disabled', 'disabled');
                  btn.setAttribute('aria-disabled', 'true');
                  btn.classList.add('bulk-backing-disabled');
                }
              });

              if (this.paymentButtonContainer) {
                this.paymentButtonContainer.style.pointerEvents = 'none';
                this.paymentButtonContainer.style.opacity = '0.5';
              }
            }
          }, 50);
          _ac.signal.addEventListener('abort', () => clearInterval(_bulkInterval));
        },

        isValid: function() {
          // Check bulk backing options
          if (this.colorSelect && this.lbSelect && this.lengthSelect) {
            const valid = !!(this.colorSelect.value && this.lbSelect.value && this.lengthSelect.value);
            if (this.instructionsSelect && !this.instructionsSelect.value) return false;
            return valid;
          }

          // Check backing instructions only
          if (this.instructionsSelect && !this.instructionsSelect.value) {
            return false;
          }

          return true;
        },

        handleButtonClick: function(e) {
          if (!this.isValid()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Show helper text and flash it
            if (this.helperText) {
              this.helperText.style.display = 'block';
              this.helperText.classList.add('show');
              this.helperText.style.animation = 'none';
              setTimeout(() => {
                this.helperText.style.animation = 'pulse 0.5s ease-in-out 2';
              }, 10);
            }

            alert('Please select all product options first.');
            return false;
          }

          // For bulk backing products with all three selects
          if (this.colorSelect && this.lbSelect && this.lengthSelect) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handleBulkBackingSubmit();
            return false;
          }

        },

        handleBulkBackingSubmit: function() {
          const colorValue = this.colorSelect.value;
          const lbValue = this.lbSelect.value;
          const lengthValue = this.lengthSelect.value;
          const lengthOption = this.lengthSelect.options[this.lengthSelect.selectedIndex];
          const lengthVariantId = lengthOption.getAttribute('data-variant-id');
          const instructionsValue = this.instructionsSelect ? this.instructionsSelect.value : null;

          // Get main variant ID
          const idSelect = form.querySelector('#product-select-' + CONFIG.productId + '[name="id"]');
          const idInput = form.querySelector('input[name="id"]');
          const mainVariantId = idSelect?.value || idInput?.value;

          // Get quantity from form
          const qtyInput = form.querySelector('input[name="quantity"]') ||
                          form.querySelector('input[type="number"]') ||
                          form.querySelector('.quantity__input');
          const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

          if (!mainVariantId) {
            alert('Error: Could not determine the product variant. Please refresh and try again.');
            return;
          }

          // Prepare main item
          const mainProperties = {
            'Color': colorValue,
            'LB': lbValue,
            'Length Spooled Onto Reel': lengthValue
          };

          if (instructionsValue) {
            mainProperties['Rigging Instructions'] = instructionsValue;
          }

          const mainItem = {
            id: mainVariantId,
            quantity: quantity,
            properties: mainProperties
          };

          // Prepare secondary item if applicable
          const secondaryItem = (lengthValue !== 'None' && lengthValue !== '100yds' && lengthVariantId) ? {
            id: lengthVariantId,
            quantity: quantity,
            properties: {
              '_Related to': 'Main Product',
              'Type': 'Length Spooling - ' + lengthValue
            }
          } : null;

          // Property matcher for cart verification
          const propertyMatcher = (cartProps, itemProps) => {
            return cartProps &&
                   cartProps['Color'] === itemProps['Color'] &&
                   cartProps['LB'] === itemProps['LB'] &&
                   cartProps['Length Spooled Onto Reel'] === itemProps['Length Spooled Onto Reel'];
          };

          // Use shared cart utility
          CartUtils.addItemsToCart(mainItem, secondaryItem, this.buttons, propertyMatcher)
            .then(() => {
              this.updateButtonState();
            })
            .catch(() => {
              this.updateButtonState();
            });
        },

        updateButtonState: function() {
          if (this.isUpdating) return;
          this.isUpdating = true;

          const isValid = this.isValid();

          this.buttons.forEach(btn => {
            // Skip if button is hidden due to sold out variant
            if (btn.classList.contains('variant-sold-out-hide')) {
              this.isUpdating = false;
              return;
            }

            if (isValid) {
              btn.disabled = false;
              btn.removeAttribute('disabled');
              btn.removeAttribute('aria-disabled');
              btn.classList.remove('bulk-backing-disabled');
            } else {
              btn.disabled = true;
              btn.setAttribute('disabled', 'disabled');
              btn.setAttribute('aria-disabled', 'true');
              btn.classList.add('bulk-backing-disabled');
            }
          });

          if (this.paymentButtonContainer) {
            if (isValid) {
              this.paymentButtonContainer.style.display = '';
              this.paymentButtonContainer.style.pointerEvents = '';
              this.paymentButtonContainer.style.opacity = '';
            } else {
              this.paymentButtonContainer.style.pointerEvents = 'none';
              this.paymentButtonContainer.style.opacity = '0.5';
            }
          }

          // Hide helper text when valid (only show it when user tries to click)
          if (this.helperText && isValid) {
            this.helperText.style.display = 'none';
            this.helperText.classList.remove('show');
          }

          this.isUpdating = false;
        }
      };

      // ========================================
      // MODULE 7: GALLERY OPTIMIZATION
      // Intercepts variant:change to replace the expensive Flickity
      // deactivate()/activate() with a lightweight reloadCells().
      // ========================================
      const GalleryOptimizer = {
        flickity: null,
        carousel: null,
        cells: null,
        thumbCells: null,
        productOptions: cfg.productOptions,
        _hasGroupedMedia: false,

        init: function() {
          var section = form.closest('.shopify-section');
          if (!section) return;

          this.carousel = section.querySelector('.product-gallery__carousel');
          if (!this.carousel) return;

          this.cells = this.carousel.querySelectorAll('.product-gallery__carousel-item');
          var thumbList = section.querySelector('.product-gallery__thumbnail-list');
          this.thumbCells = thumbList ? thumbList.querySelectorAll('.product-gallery__thumbnail') : [];

          // Check if any cell has grouped media
          for (var i = 0; i < this.cells.length; i++) {
            if (this.cells[i].hasAttribute('data-group-name')) {
              this._hasGroupedMedia = true;
              break;
            }
          }

          // Only intercept if there's grouped media (otherwise theme.js just does selectCell which is fast)
          if (!this._hasGroupedMedia) return;

          // Intercept variant:change on form before it bubbles to section
          // (theme.js listens on the section element)
          form.addEventListener('variant:change', this._onVariantChange.bind(this), _sig);
        },

        _getFlickity: function() {
          if (this.flickity) return this.flickity;
          if (window.Flickity && window.Flickity.data) {
            this.flickity = window.Flickity.data(this.carousel);
          }
          return this.flickity;
        },

        _onVariantChange: function(e) {
          var variant = e.detail && e.detail.variant;
          if (!variant) return;

          var flkty = this._getFlickity();
          if (!flkty) return; // Can't optimize, let theme.js handle it

          // Stop propagation to prevent theme.js from doing expensive deactivate/activate
          e.stopPropagation();

          var changed = false;

          // Toggle is-filtered classes (mirrors theme.js variantHasChanged logic)
          for (var i = 0; i < this.cells.length; i++) {
            var cell = this.cells[i];
            if (!cell.hasAttribute('data-group-name')) continue;

            var groupName = cell.getAttribute('data-group-name');
            var shouldShow = false;

            for (var oi = 0; oi < this.productOptions.length; oi++) {
              if (this.productOptions[oi].toLowerCase() === groupName) {
                var optValue = variant['option' + (oi + 1)];
                if (optValue && optValue.toLowerCase() === cell.getAttribute('data-group-value')) {
                  shouldShow = true;
                }
                if (variant.featured_media && variant.featured_media.id === parseInt(cell.getAttribute('data-media-id'))) {
                  shouldShow = true;
                }
                break;
              }
            }

            var wasFiltered = cell.classList.contains('is-filtered');
            if (shouldShow) {
              cell.classList.remove('is-filtered');
              if (this.thumbCells[i]) this.thumbCells[i].classList.remove('is-filtered');
            } else {
              cell.classList.add('is-filtered');
              if (this.thumbCells[i]) this.thumbCells[i].classList.add('is-filtered');
            }

            if (wasFiltered !== cell.classList.contains('is-filtered')) {
              changed = true;
            }
          }

          // Only reload Flickity cells if filtering actually changed
          if (changed) {
            flkty.reloadCells();
            flkty.resize();
          }

          // Reveal hidden slides on desktop (mirrors theme.js Responsive.matchesBreakpoint("lap-and-up"))
          if (window.matchMedia('screen and (min-width: 1024px)').matches) {
            var slides = this.carousel.querySelectorAll('.product-gallery__carousel-item');
            for (var s = 0; s < slides.length; s++) {
              slides[s].classList.remove('product-gallery__carousel-item--hidden');
            }
          }

          // Select featured media cell
          if (flkty && variant.featured_media) {
            flkty.selectCell('[data-media-id="' + variant.featured_media.id + '"]');
          }
        }
      };

      // ========================================
      // INITIALIZATION
      // ========================================
      function initAll() {
        DropdownFilter.init();
        DropdownSwatchFilter.init();
        VariantSync.init();
        CartButtonVisibility.init();
        AffirmIntegration.init();
        BackingOptions.init();
        BulkBackingOptions.init();
        GalleryOptimizer.init();
      }

      // Re-init on events (registered ONCE, outside initAll to prevent stacking)
      document.addEventListener('product:rerendered', initAll, _sig);
      document.addEventListener('shopify:section:load', initAll, _sig);

      // Start initialization
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll, _sig);
      } else {
        initAll();
      }

      // Retry if elements not ready
      if (!form.querySelector('select.product-form__single-selector')) {
        setTimeout(initAll, 200);
      }
  }

  function flush() {
    var q = window.__tvsQueue || [];
    window.__tvsQueue = [];
    for (var i = 0; i < q.length; i++) {
      try { run(q[i]); } catch (e) { if (window.console) console.error('[variant-selector]', e); }
    }
  }

  window.TridentVariantSelector = { run: run, flush: flush };
  flush();
})();
